import { colorTokens, KariusLink } from '@kariusdx/design-system';
import { ChevronDownIcon } from '@kariusdx/design-system';
import { Accordion, AccordionSummary, Typography, AccordionDetails, Grid, styled, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { logKariusEvent, EngagementEvents } from '../Analytics';

interface HelpAccordionProps {
  title: string;
  details: string | ReactNode;
  index: number;
}

export const LoginHelpSection = () => {
  const theme = useTheme();
  return (
    <Grid
      justifyContent='center'
      flexDirection='column'
      sx={{
        maxWidth: '428px',
        padding: {
          md: '0px',
          xs: '0px 31px',
        },
      }}
    >
      <Typography variant='heading_01'>Need help?</Typography>
      <Grid container flexDirection='column' sx={{ marginTop: theme.spacing(2) }}>
        <HelpAccordion
          index={1}
          title='Setting up or signing in to your account'
          details={
            <>
              If your institution doesn’t have an account yet with Karius, you need a digital user account for the
              Karius Portal or Karius Mobile App, or need help signing in to your account, Karius is happy to assist
              you:{' '}
              <KariusLink href={'tel:8664527487'} underline onClick={() => logLink('phone')} className='link-text'>
                (866) 452-7487
              </KariusLink>{' '}
              or{' '}
              <KariusLink
                href={'mailto:help@kariusdx.com'}
                underline
                onClick={() => logLink('email')}
                className='link-text'
              >
                help@kariusdx.com
              </KariusLink>
              .
            </>
          }
        />
        <HelpAccordion
          index={2}
          title='How to send specimens'
          details={
            <>
              Please see the{' '}
              <a
                href='https://karius.showpad.com/share/eMdS8q9YeDVU1NyoHG4aS'
                onClick={() => logLink('specimen_instructions')}
                target='_blank'
                rel='noreferrer'
                className='link-text'
              >
                Karius Spectrum Specimen Collection, Preparation, and Shipping Instructions.
              </a>
            </>
          }
        />
        <HelpAccordion
          index={3}
          title='When are specimens processed?'
          details='Specimens are received Monday-Saturday and results are reported Tuesday-Sunday.'
        />
        <HelpAccordion
          index={4}
          title='Why doesn’t the Karius Spectrum test detect the coronavirus?'
          details={
            <>
              The Karius Spectrum test detects microbial cell-free DNA from bacteria, DNA viruses, fungi, and parasites.
              It does not currently detect RNA viruses such as the coronavirus. Our{' '}
              <a
                href='https://kariusdx.com/our-solution/karius-spectrum'
                onClick={() => logLink('clinical_faq')}
                target='_blank'
                rel='noreferrer'
                className='link-text'
              >
                Clinical FAQs
              </a>{' '}
              offer more detail on potential applications for the test.
            </>
          }
        />
      </Grid>
    </Grid>
  );
};

const HelpAccordion = ({ title, details, index }: HelpAccordionProps) => {
  return (
    <StyledAccordion>
      <AccordionSummary
        expandIcon={<ChevronDownIcon />}
        aria-controls={`panel${index}a-content`}
        id={`panel${index}a-header`}
        onClick={() => logAccordion(title)}
      >
        <Typography
          variant='body_01'
          sx={{
            color: colorTokens.text_link,
            marginLeft: '4px',
          }}
        >
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant='body_01' component='div'>
          {details}
        </Typography>
      </AccordionDetails>
    </StyledAccordion>
  );
};

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  '&.MuiAccordion-root': {
    boxShadow: 'none',
    margin: '0px',
    '&:before': {
      backgroundColor: colorTokens.ui_01,
    },
    '& .MuiAccordionSummary-root .MuiAccordionSummary-expandIconWrapper': {
      color: colorTokens.text_link,
      '& svg': {
        width: '10px',
        height: '10px',
      },
    },
    '& .MuiAccordionSummary-content': {
      margin: '0px 0px 0px 4px',
    },
    '& .MuiAccordionSummary-root': {
      flexDirection: 'row-reverse',
      padding: theme.spacing(0),
      minHeight: '0px',
      marginTop: '6px',
      marginBottom: '6px',
    },
    '& .MuiAccordionDetails-root': {
      padding: '0 4px 9px 24px',
      '&.Mui-expanded': {
        minHeight: 0,
        height: '26px',
      },
    },
  },
}));

const logAccordion = (title: string) => {
  logKariusEvent(EngagementEvents.need_help_accordion_tapped, { title });
};

const logLink = (link: 'phone' | 'email' | 'clinical_faq' | 'specimen_instructions') => {
  logKariusEvent(EngagementEvents.need_help_link_tapped, { link });
};
